import React, { Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './IconCard.css';

const Lottie = React.lazy(() => import('lottie-react'));

interface IconCardProps {
  iconClass?: string;
  lottieData?: object | null;
  heading: React.ReactNode;
  children?: React.ReactNode;
  link?: string;
  target?: string;
  rel?: string;
  linkClassName?: string;
  cardClassName?: string;
}

const IconCard: React.FC<IconCardProps> = ({
  iconClass,
  lottieData,
  heading,
  children,
  link,
  target = '_self',
  cardClassName = '',
}) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!link) return;
    // Don't navigate if the user clicked on an inner link
    const targetEl = e.target as HTMLElement;
    if (targetEl.closest('a')) return;

    if (target === '_blank') {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="icon-card-wrapper h-100"
    >
      <div
        className={`icon-card p-4 rounded text-center h-100 ${cardClassName}`}
        onClick={link ? handleClick : undefined}
        style={link ? { cursor: 'pointer' } : undefined}
        role={link ? 'link' : undefined}
      >
        {lottieData ? (
          <div className="lottie-container mb-3 d-flex justify-content-center">
            <Suspense fallback={<div style={{ width: 80, height: 80 }} />}>
              <Lottie
                animationData={lottieData}
                loop={true}
                style={{ width: 80, height: 80 }}
              />
            </Suspense>
          </div>
        ) : iconClass ? (
          <i className={`fs-1 ${iconClass} mb-3 d-block`}></i>
        ) : null}
        {heading && <h5 className="fw-bold">{heading}</h5>}
        {children && <div className="mt-3">{children}</div>}
      </div>
    </motion.div>
  );
};

export default IconCard;

